import React from 'react';
import LegalPageLayout from '../../components/Legal/LegalPageLayout';

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'payment-processor', label: 'Payment Processor' },
  { id: 'accepted-methods', label: 'Accepted Methods' },
  { id: 'billing', label: 'Billing & Auto-Renewal' },
  { id: 'refund-policy', label: 'Refund Policy' },
  { id: 'cancellation', label: 'Cancellation Policy' },
  { id: 'failed-payments', label: 'Failed Payments' },
  { id: 'taxes', label: 'GST & Taxes' },
  { id: 'security', label: 'Payment Security' },
  { id: 'disputes', label: 'Disputes & Chargebacks' },
  { id: 'contact', label: 'Contact' },
];

const PaymentPolicy = () => (
  <LegalPageLayout
    title="Payment Policy"
    subtitle="Everything you need to know about payments, subscriptions, refunds, and billing."
    lastUpdated="May 7, 2025"
    sections={sections}
  >
    <div className="legal-section" id="overview" data-section>
      <h2>1. Overview</h2>
      <p>
        This Payment Policy governs all financial transactions made in connection with CRM Automax
        subscriptions and services. By purchasing a subscription or initiating any payment on our
        platform, you agree to the terms outlined in this policy.
      </p>
      <p>
        This policy should be read in conjunction with our <a href="/terms">Terms & Conditions</a> and
        <a href="/privacy-policy"> Privacy Policy</a>. Capitalized terms not defined here have the
        meanings given in our Terms & Conditions.
      </p>
      <div className="legal-highlight">
        <p>
          <strong>Razorpay Merchant:</strong> CRM Automax processes all payments through Razorpay,
          an RBI-authorised Payment Aggregator. All transactions are subject to Razorpay's terms
          and Indian financial regulations.
        </p>
      </div>
    </div>

    <div className="legal-section" id="payment-processor" data-section>
      <h2>2. Payment Processor — Razorpay</h2>
      <p>
        CRM Automax uses <strong>Razorpay Financial Solutions Pvt. Ltd.</strong> as its exclusive
        payment gateway. Razorpay is:
      </p>
      <ul>
        <li>Authorised by the Reserve Bank of India (RBI) as a Payment Aggregator</li>
        <li>PCI-DSS Level 1 certified — the highest level of payment security</li>
        <li>Compliant with Indian data localisation requirements (all payment data stored in India)</li>
      </ul>
      <p>
        When you make a payment, you are redirected to or interact with Razorpay's secure payment
        interface. CRM Automax does <strong>not</strong> receive, store, or process your complete
        card number, CVV, UPI PIN, or net banking credentials.
      </p>
      <p>
        Razorpay's Terms of Service: <a href="https://razorpay.com/terms/" target="_blank" rel="noreferrer">razorpay.com/terms</a>
        <br />Razorpay's Privacy Policy: <a href="https://razorpay.com/privacy/" target="_blank" rel="noreferrer">razorpay.com/privacy</a>
      </p>
    </div>

    <div className="legal-section" id="accepted-methods" data-section>
      <h2>3. Accepted Payment Methods</h2>
      <p>We accept the following payment methods via Razorpay:</p>
      <table className="legal-table">
        <thead>
          <tr><th>Method</th><th>Details</th></tr>
        </thead>
        <tbody>
          <tr><td>UPI</td><td>All UPI apps (Google Pay, PhonePe, Paytm, BHIM, etc.)</td></tr>
          <tr><td>Credit / Debit Cards</td><td>Visa, Mastercard, RuPay, American Express</td></tr>
          <tr><td>Net Banking</td><td>50+ Indian banks supported</td></tr>
          <tr><td>Wallets</td><td>Paytm, Amazon Pay, and other RBI-approved wallets</td></tr>
          <tr><td>EMI</td><td>Available on eligible cards (subject to bank approval)</td></tr>
        </tbody>
      </table>
      <p>
        All transactions are processed in <strong>Indian Rupees (INR ₹)</strong>. International
        cards may be subject to additional foreign transaction fees charged by your bank.
      </p>
    </div>

    <div className="legal-section" id="billing" data-section>
      <h2>4. Billing Cycle & Auto-Renewal</h2>
      <h3>4.1 Billing Frequency</h3>
      <p>
        Subscriptions are billed in advance at the start of each billing period. You may choose
        from monthly or annual plans as available at the time of purchase.
      </p>
      <h3>4.2 Auto-Renewal</h3>
      <p>
        Your subscription will automatically renew at the end of each billing period using the
        same payment method unless you cancel before the renewal date.
      </p>
      <ul>
        <li>We will send a renewal reminder email <strong>7 days before</strong> the renewal date</li>
        <li>Renewal charges will be applied at the then-current plan price</li>
        <li>Price changes will be communicated at least 30 days in advance</li>
      </ul>
      <h3>4.3 Invoices</h3>
      <p>
        A GST-compliant invoice will be emailed to your registered email address after each
        successful payment. Invoices can also be downloaded from your account dashboard.
      </p>
    </div>

    <div className="legal-section" id="refund-policy" data-section>
      <h2>5. Refund Policy</h2>
      <div className="legal-highlight">
        <p>
          <strong>Summary:</strong> Annual plans have a 7-day refund window from the date of purchase.
          Monthly plans are non-refundable. Trial conversions must be cancelled before renewal to
          avoid charges.
        </p>
      </div>

      <h3>5.1 Annual Plans — Refund Eligibility</h3>
      <p>
        If you purchase an annual subscription and are not satisfied, you may request a full refund
        within <strong>7 calendar days</strong> of the initial purchase date, provided:
      </p>
      <ul>
        <li>The refund request is submitted to <a href="mailto:info@westernflex.com">info@westernflex.com</a></li>
        <li>The account has not been used extensively (fewer than 50 leads created)</li>
        <li>No data export has been requested</li>
        <li>This is your first annual subscription (not a renewal)</li>
      </ul>

      <h3>5.2 Monthly Plans — No Refund</h3>
      <p>
        Monthly subscription payments are <strong>non-refundable</strong>. You may cancel to stop
        future billing, but no refund will be issued for the current month.
      </p>

      <h3>5.3 Renewals — No Refund</h3>
      <p>
        Renewal charges (monthly or annual) are <strong>non-refundable</strong> once processed.
        It is your responsibility to cancel before the renewal date if you do not wish to continue.
      </p>

      <h3>5.4 Non-Refundable Items</h3>
      <ul>
        <li>Setup or onboarding fees (if applicable)</li>
        <li>Custom development or professional services</li>
        <li>Partial months or days of service</li>
        <li>Accounts suspended for Terms violations</li>
      </ul>

      <h3>5.5 Refund Process & Timeline</h3>
      <p>To request a refund:</p>
      <ol>
        <li>Email <a href="mailto:info@westernflex.com">info@westernflex.com</a> with subject "Refund Request — [Your Company Name]"</li>
        <li>Include your registered email, Razorpay payment ID, and reason for refund</li>
        <li>Our team will review and respond within <strong>3 business days</strong></li>
        <li>Approved refunds are processed back to the original payment method</li>
        <li>Credit reflects in your account within <strong>5–10 business days</strong> depending on your bank</li>
      </ol>
      <p>
        Note: While Razorpay does not charge a fee for refunds, any payment gateway fees or GST
        collected at the time of the original transaction may not be reversed.
      </p>
    </div>

    <div className="legal-section" id="cancellation" data-section>
      <h2>6. Cancellation Policy</h2>
      <p>
        You may cancel your subscription at any time. Cancellation prevents future billing but does
        not delete your account or data immediately.
      </p>
      <h3>6.1 How to Cancel</h3>
      <ul>
        <li>Email <a href="mailto:info@westernflex.com">info@westernflex.com</a> with "Cancel Subscription" in the subject</li>
        <li>Provide your registered email and company name</li>
        <li>Cancellation takes effect at the end of the current billing period</li>
      </ul>
      <h3>6.2 After Cancellation</h3>
      <ul>
        <li>You retain access to the platform until the end of the paid period</li>
        <li>Your data is retained for 30 days post-expiry for export</li>
        <li>After 30 days, all data is permanently deleted</li>
        <li>You may reactivate your account within 30 days without data loss</li>
      </ul>
    </div>

    <div className="legal-section" id="failed-payments" data-section>
      <h2>7. Failed Payments</h2>
      <p>If a renewal payment fails, the following process applies:</p>
      <table className="legal-table">
        <thead>
          <tr><th>Day</th><th>Action</th></tr>
        </thead>
        <tbody>
          <tr><td>Day 0</td><td>Payment fails — email notification sent</td></tr>
          <tr><td>Day 1–3</td><td>Automatic retry attempts by Razorpay</td></tr>
          <tr><td>Day 4</td><td>Account access restricted (read-only mode)</td></tr>
          <tr><td>Day 7</td><td>Account suspended — data retained</td></tr>
          <tr><td>Day 37</td><td>Account and data permanently deleted</td></tr>
        </tbody>
      </table>
      <p>
        To reactivate, update your payment method and complete payment within the grace period.
        Contact <a href="mailto:info@westernflex.com">info@westernflex.com</a> for assistance.
      </p>
    </div>

    <div className="legal-section" id="taxes" data-section>
      <h2>8. GST & Taxes</h2>
      <p>
        All prices displayed on our platform are exclusive of Goods and Services Tax (GST) unless
        explicitly stated otherwise. GST at the applicable rate (currently 18% for software services)
        will be added at checkout.
      </p>
      <ul>
        <li>GST invoices are issued for every transaction</li>
        <li>If you require a GST-compliant invoice with your GSTIN, provide it during registration or account setup</li>
        <li>International customers may be subject to different tax treatment</li>
      </ul>
    </div>

    <div className="legal-section" id="security" data-section>
      <h2>9. Payment Security</h2>
      <p>Your payment security is our highest priority:</p>
      <ul>
        <li>All payment data is transmitted over TLS-encrypted connections (HTTPS)</li>
        <li>Payments are processed on Razorpay's PCI-DSS Level 1 certified servers</li>
        <li>CRM Automax <strong>never stores</strong> complete card numbers, CVVs, or bank passwords</li>
        <li>We store only transaction reference IDs and masked payment identifiers</li>
        <li>All payment data is localised within India per RBI requirements</li>
      </ul>
    </div>

    <div className="legal-section" id="disputes" data-section>
      <h2>10. Disputes & Chargebacks</h2>
      <h3>10.1 Raising a Dispute</h3>
      <p>
        If you believe you have been incorrectly charged, please contact us <strong>before</strong>
        initiating a chargeback with your bank:
      </p>
      <ol>
        <li>Email <a href="mailto:info@westernflex.com">info@westernflex.com</a> with full transaction details</li>
        <li>We will investigate and respond within 3 business days</li>
        <li>Most disputes are resolved through direct communication without requiring a chargeback</li>
      </ol>
      <h3>10.2 Chargebacks</h3>
      <p>
        Initiating a chargeback without first contacting us may result in immediate account
        suspension. Fraudulent chargebacks will be contested and reported. Accounts under chargeback
        review will have restricted access until the matter is resolved.
      </p>
      <h3>10.3 RBI Compliance</h3>
      <p>
        For failed or cancelled transactions, refunds are processed per RBI guidelines for
        Payment Aggregators. If you experience delays beyond 10 business days for an approved
        refund, please contact us and we will escalate with Razorpay.
      </p>
    </div>

    <div className="legal-section" id="contact" data-section>
      <h2>11. Contact for Payment Issues</h2>
      <table className="legal-table">
        <tbody>
          <tr><td><strong>Billing Email</strong></td><td><a href="mailto:info@westernflex.com">info@westernflex.com</a></td></tr>
          <tr><td><strong>General Support</strong></td><td><a href="mailto:info@westernflex.com">info@westernflex.com</a></td></tr>
          <tr><td><strong>Response Time</strong></td><td>Within 3 business days</td></tr>
          <tr><td><strong>Razorpay Support</strong></td><td><a href="https://razorpay.com/support/" target="_blank" rel="noreferrer">razorpay.com/support</a></td></tr>
        </tbody>
      </table>
    </div>
  </LegalPageLayout>
);

export default PaymentPolicy;
